// Phase II · Wave E.1a · Shared currency constants (mirror of backend
// ``backend/constants/currencies.py``). Keep in sync when the backend
// map changes.
export const SUPPORTED_CURRENCIES = [
    "USD", "EUR", "GBP", "JPY", "CNY",
    "INR", "BRL", "MXN", "CAD", "AUD",
    "CHF", "SGD", "HKD", "KRW", "SEK",
];

export const DEFAULT_CURRENCY = "USD";

export const CURRENCY_DECIMALS = {
    USD: 2, EUR: 2, GBP: 2, JPY: 0, CNY: 2,
    INR: 2, BRL: 2, MXN: 2, CAD: 2, AUD: 2,
    CHF: 2, SGD: 2, HKD: 2, KRW: 0, SEK: 2,
};

export const CURRENCY_SYMBOL = {
    USD: "$",  EUR: "€",  GBP: "£",  JPY: "¥",  CNY: "¥",
    INR: "₹",  BRL: "R$", MXN: "$",  CAD: "$",  AUD: "$",
    CHF: "Fr", SGD: "$",  HKD: "$",  KRW: "₩",  SEK: "kr",
};

export const CURRENCY_SYMBOL_AFTER = new Set(["CHF", "SEK"]);

export const LOCALE_TO_CURRENCY = {
    // Phase VII.I · Currency USD enforcement · all locales canonically
    // map to USD so pricing displays stay consistent with Stripe
    // settlement currency. Historical EUR/GBP/JPY/CNY/KRW/INR/SEK map
    // kept here as comments for reference only.
    "en-US": "USD", "en-GB": "USD",
    "it": "USD", "de": "USD", "fr": "USD", "es": "USD", "nl": "USD",
    "fi": "USD", "hr": "USD", "sk": "USD", "sl": "USD", "et": "USD",
    "lv": "USD", "lt": "USD", "el": "USD", "ca": "USD", "eu": "USD",
    "pt-BR": "USD", "ja": "USD", "zh-CN": "USD", "ko": "USD",
    "hi": "USD",   "sv": "USD",
    "pl": "USD", "da": "USD", "no": "USD", "cs": "USD", "hu": "USD",
    "ro": "USD", "uk": "USD", "bg": "USD",
    "ar": "USD", "ru": "USD", "tr": "USD", "vi": "USD", "th": "USD",
    "id": "USD", "ms": "USD", "fil": "USD", "he": "USD", "af": "USD",
};

export function currencyForLocale(locale) {
    if (!locale) return DEFAULT_CURRENCY;
    const s = String(locale).trim();
    if (LOCALE_TO_CURRENCY[s]) return LOCALE_TO_CURRENCY[s];
    const base = s.split("-")[0];
    if (LOCALE_TO_CURRENCY[base]) return LOCALE_TO_CURRENCY[base];
    return DEFAULT_CURRENCY;
}

/**
 * Format an amount with the ISO 4217 fractional-digits + symbol
 * placement. Server-side helper is in ``currency_display_service.py``.
 */
export function formatPrice(amount, currency) {
    const cur = String(currency || DEFAULT_CURRENCY).toUpperCase();
    const decimals = CURRENCY_DECIMALS[cur] ?? 2;
    const num = Number(amount);
    if (!Number.isFinite(num)) return String(amount);
    let formatted;
    try {
        formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(num);
    } catch {
        formatted = num.toFixed(decimals);
    }
    const symbol = CURRENCY_SYMBOL[cur] || `${cur} `;
    return CURRENCY_SYMBOL_AFTER.has(cur)
        ? `${formatted} ${symbol}`
        : `${symbol}${formatted}`;
}
